
import { mdiPail } from '@mdi/js'
import SidebarProfileSelector from 'components/App/components/AppNavigationSidebar/publishing/SidebarProfileSelector'
import { HelpToggleButton } from 'components/App/components/HelpToggleButton'
import { BucketCard } from 'components/Buckets'
import { NOTIFICATION_DURATION_LONG } from 'components/ConnectedSnackbar'
import EmptyView from 'components/EmptyView'
import PostFilesDialog from 'components/Posts/PostFilesDialog'
import { RecycleDialog } from 'components/Posts/components/RecycleSelector'
import ScrollListener from 'components/ScrollListener'
import SimpleLoader from 'components/SimpleLoader'
import SocialIcon from 'components/SocialIcon'
import { format } from 'date-fns'
import { useEffectUpdateOnly } from 'hooks/useEffectUpdateOnly'
import {
  PlannedPost,
  PostBase,
  PostedPost
} from 'interfaces'
import { POST_TYPES } from 'interfaces/Content/ContentType'
import { Fragment, useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { FormattedMessage, useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { NavLink, useLocation, useNavigate } from 'react-router'
import { BulkPostActionsProps } from 'routes/publishing/components/BulkActionsMenus/BulkPostActions'
import PostedSortMenu, { PostedSortBy, SortDirection } from 'routes/publishing/components/BulkActionsMenus/PostedSortMenu'
import CopyPostsPopup from 'routes/publishing/components/CopyPostsPopup'
import BucketUpdatePopup from 'routes/publishing/routes/buckets/components/BucketUpdatePopup'
import FailedPostsManager from 'routes/publishing/routes/posts/FailedPostsManager'
import {
  addPosts,
  recyclePostsSuccess,
  replacePost,
  setPosts,
  updateRecyclePost
} from 'routes/publishing/routes/posts/actions'
import PlannedPostsList from 'routes/publishing/routes/posts/components/PlannedPostsList'
import PostPreviewWithInfo from 'routes/publishing/routes/posts/components/PostPreviewWithInfo'
import PostedPostsList from 'routes/publishing/routes/posts/components/PostedPostsList'
import PublishingTopBar from 'routes/publishing/routes/posts/components/PublishingTopBar'
import { postsReducer } from 'routes/publishing/routes/posts/reducer'
import { getBulkMovePostsResult, getMovedPostResult, getReorderedPosts } from 'routes/publishing/routes/posts/utils/reorder'
import { of as observableOf, Subject } from 'rxjs'
import { catchError, concatMap, exhaustMap, filter, map, mergeMap, tap } from 'rxjs/operators'
import { getDerivedStateFromPost, mergeState, resetComposer, setComposerBucket } from 'services/compose'
import { connectedDestinationsSelector } from 'services/destinations'
import { initPlans } from 'services/plan/actions'
import { planToProfilesSelector } from 'services/plan/selectors'
import {
  bulkDeletePosts,
  getBucketById,
  getBucketPosts,
  getBuckets,
  getPostById,
  getPostedPosts,
  recyclePosts,
  reorderPosts,
  requeuePost,
  shufflePosts,
  updatePostRecycle
} from 'services/post/actions'
import { postedAction, PostedPostsAction } from 'services/post/archive/actions'
import { newPostSelector } from 'services/post/selectors'
import { publishPosts } from 'services/post/shareNow/actions'
import { AutoPostSlot } from 'services/post/state'
import { checkFeatureAvailability } from 'services/product'
import { setComposerProfiles } from 'services/shared/actions'
import { message } from 'services/snackbar'
import { ContentType, groupByKey, PostDestination } from 'shared'
import {
  FEATURE_POST_COPY,
  FEATURE_POST_INSIGHTS_ACTIONS_REPLAN,
  FEATURE_POST_INSIGHTS_ACTIONS_SORT,
  FEATURE_POST_RECYCLE,
  FEATURE_POST_SHUFFLE
} from 'shared/constants'
import { Bucket, PostDestinationType } from 'shared/types'
import { sortNetworksDefault } from 'shared/utils'
import { StoreThunkDispatch } from 'store/state'
import { sortByKeyAlphabetically } from 'utils/sort/order'
import styles from './BucketPostsView.pcss'
import { BucketProfile } from './BucketProfile'

const SCROLL_TRESHOLD = 350

interface BucketPostsViewProps {
  bucket: Bucket
  activeView: 'planned' | 'posted' | 'failed'
  onBucketPostsUpdated?: () => void
}

export function BucketPostsView(props: BucketPostsViewProps) {
  const intl = useIntl()
  const isFailedView = props.activeView === 'failed'
  const { onBucketPostsUpdated } = props
  const dispatch = useDispatch<StoreThunkDispatch>()
  const location = useLocation()
  const navigate = useNavigate()
  const connectedProfiles = useSelector(connectedDestinationsSelector)
  const planProfilesMap = useSelector(planToProfilesSelector)
  const [selectedProfilePpid, setSelectedProfilePpid] = useState<string | undefined>(props.bucket.ppids[0])
  const bucketPpidsString = props.bucket.ppids.join(',')
  const [refreshKey, setRefreshKey] = useState(0)
  const [postInPreview, setPostInPreview] = useState<PlannedPost | null>(null)
  const [withPreview, setWithPreview] = useState(false)
  const [loading, setLoading] = useState(false)
  const [loadingOverlayActive, setLoadingOverlayActive] = useState(false)
  const [posts, postsDispatch] = useReducer(postsReducer, [] as PlannedPost[])
  const postsRef = useRef<PlannedPost[]>(posts)
  const [autoPostSlots, setAutoPostSlots] = useState<AutoPostSlot[]>([])
  const autoPostSlotsRef = useRef<AutoPostSlot[]>(autoPostSlots)
  const [selectedPostsIds, setSelectedPostsIds] = useState<Record<string, boolean> | 'all'>({})
  const [postsToCopy, setPostsToCopy] = useState<PlannedPost[]>([])
  const [postInMediaPreviewDialog, setPostInMediaPreviewDialog] = useState<PostBase | null>(null)
  const [totalPosts, setTotalPosts] = useState(0)
  const [updatePopupOpen, setUpdatePopupOpen] = useState(false)
  const [search, setSearch] = useState<{ query: string, types: ContentType[], searchInSummary: boolean }>(
    { query: '', types: POST_TYPES, searchInSummary: true }
  )
  const [sidebarActiveView, setSidebarActiveView] = useState<'grid' | 'list' | 'network'>('grid')
  const newPostTimestamp = useSelector(newPostSelector)
  const [sortBy, setSortBy] = useState<PostedSortBy>(PostedSortBy.Time)
  const [sortDirection, setSortDirection] = useState<SortDirection>('descending')
  const [isEmptyViewClosed, setIsEmptyViewClosed] = useState(false)
  const [recycleDialogOpen, setRecycleDialogOpen] = useState(false)

  const requeue$ = useRef<Subject<{
    postIds: string[], ppids: string[], all: boolean, query?: string, types?: ContentType[]
  }>>(undefined)
  const pageRef = useRef(0)
  const hasNextRef = useRef(true)
  const ppidRef = useRef('')
  const delete$ = useRef<Subject<{
    postIds: string[], ppids: string[], all: boolean, query?: string, types?: ContentType[]
  }>>(undefined)
  const selectionRef = useRef<{ postIds: string[], profileIds: string[], ppids: string[], all: boolean }>(undefined)
  const fetch$ = useRef<Subject<{
    type: 'planned' | 'posted',
    query?: string,
    types?: ContentType[],
    sortBy?: PostedSortBy,
    sortDirection?: SortDirection,
    searchInSummary?: boolean
    archivedOnly?: boolean
  }>>(undefined)
  const shuffle$ = useRef<Subject<{ postIds: string[], ppids: string[], all?: boolean }>>(undefined)
  const reorder$ = useRef<Subject<{ ids: string[], moveTo?: 'top' | 'bottom' }>>(undefined)
  const recycle$ = useRef<Subject<{ postId: string, value: boolean | Date | number }>>(undefined)
  const share$ = useRef<Subject<{
    postIds: string[],
    ppids: string[],
    bucketId?: string
  }>>(undefined)
  const batchRecycle$ = useRef<Subject<{
    postIds: string[], ppids: string[], all?: boolean, query?: string, types?: ContentType[], value?: boolean | Date | number
  }>>(undefined)

  const postedAction$ = useRef<Subject<{
    action: PostedPostsAction,
    postIds: string[],
    ppids: string[],
    all: boolean,
    query?: string,
    types?: ContentType[]
  }>>(undefined)

  const postsMap = useMemo(() => new Map(posts.map(post => [post.id, post])), [posts])

  const refreshPosts = useCallback(() => {
    setRefreshKey(current => current + 1)
  }, [])

  useEffect(() => {
    dispatch(initPlans())
  }, [dispatch])

  const closeEmptyView = () => {
    setIsEmptyViewClosed(true)
  }

  useEffect(() => {
    ppidRef.current = selectedProfilePpid || ''
  }, [selectedProfilePpid])

  useEffect(() => {
    // EXPL: keep latest posts/slots in refs so item callbacks stay referentially
    // stable across posts updates and don't force every list item to re-render
    postsRef.current = posts
  }, [posts])

  useEffect(() => {
    autoPostSlotsRef.current = autoPostSlots
  }, [autoPostSlots])

  useEffect(() => {
    if (!props.bucket.ppids.includes(selectedProfilePpid as string)) {
      setSelectedProfilePpid(props.bucket.ppids[0])
    }
    setWithPreview(false)
    setPostInPreview(null)
  }, [props.bucket.id, props.bucket.ppids, selectedProfilePpid])

  useEffect(() => {
    if (location.state?.updatedPostHash) {
      const updatedPostId = location.state.updatedPostHash.split('-')[0]
      dispatch(getPostById(updatedPostId))
        .unwrap()
        .then((post: any) => {
          postsDispatch(replacePost(post))
        })
        .catch(refreshPosts)
    }
    if (location.state?.search) {
      setSearch(location.state.search)
    }
    if (location.state?.ppid) {
      setSelectedProfilePpid(location.state.ppid)
    }
  }, [location.state?.search, location.state?.refresh, location.state?.ppid, location.state?.updatedPostHash, dispatch, refreshPosts])

  const previewSelection = useMemo(() => {
    const selectedPostIndex = postInPreview
      ? posts.findIndex((p: any) => p.id === postInPreview.id)
      : -1
    let hasNext = false
    let hasPrev = false

    if (selectedPostIndex !== -1) {
      hasNext = selectedPostIndex < posts.length - 1
      hasPrev = selectedPostIndex >= 1
    }
    return {
      selectedPostIndex,
      hasNext,
      hasPrev
    }
  }, [postInPreview, posts])

  // Update posts bucket info
  useEffect(() => {
    postsDispatch(setPosts<PlannedPost>(posts.map((p: PlannedPost) => ({ ...p, bucket: props.bucket }))))
    setPostInPreview(current => {
      if (!current) {
        return null
      }
      return { ...current, bucket: props.bucket }
    })
    // EXPL: intentionally keyed on posts.length + bucket name/color, NOT the full `posts` array
    // or `props.bucket`. This effect dispatches setPosts(posts.map(...)) which replaces the `posts`
    // reducer state with a fresh array — listing `posts` as a dependency would loop infinitely.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.bucket.name, props.bucket.color, posts.length])

  useEffect(() => {
    postedAction$.current = new Subject()
    postedAction$.current.pipe(concatMap(({ action, ppids, postIds, all, query, types }) => {
      return dispatch(postedAction(action, postIds, ppids, all, undefined, query, types))
        .pipe(
          catchError((error) => {
            if (error.message) {
              dispatch(message(error.message, 'error', NOTIFICATION_DURATION_LONG))
            } else {
              dispatch(message(intl.formatMessage({ id: 'errors.generic' }), 'error'))
            }
            return observableOf({ error })
          }),
          map(response => ({ ...response, action }))
        )
    }))
      .subscribe((response: any) => {
        if (!response.error) {
          const postIds = response.postIds
          if (postIds && postIds.length > 0 && (response.action === 'archive' || response.action === 'unarchive')) {
            setRefreshKey(current => current + 1)
            setSelectedPostsIds({})
          }
          dispatch(message(intl.formatMessage({
            id: `notifications.posts-${response.action === 'archive' ? 'archived' : 'unarchived'}`
          }), 'success'))
        }
      })

    return () => postedAction$.current?.unsubscribe()
  }, [dispatch, intl, posts])

  useEffect(() => {
    share$.current = new Subject()
    share$.current
      .pipe(
        tap(() => {
          setLoadingOverlayActive(true)
        }),
        mergeMap(({ ppids, postIds, bucketId }) => {
          return dispatch(publishPosts(postIds, ppids, 'planned', false, bucketId))
            .pipe(catchError((error: Error) => {
              dispatch(message(intl.formatMessage({ id: 'notifications.error-w-message' }, { error: error.message }), 'error'))
              return observableOf({ error })
            }))
        })
      )
      .subscribe((response: any) => {
        if (!response.error) {
          setSelectedPostsIds({})
          dispatch(message(intl.formatMessage({ id: 'notifications.post-posted' }), 'success'))
          setRefreshKey(current => current + 1)
        }
        setLoadingOverlayActive(false)
      })

    return () => share$.current?.unsubscribe()
  }, [dispatch, intl])

  const onShareNow = useCallback((post: PlannedPost) => {
    share$.current?.next({ postIds: [post.id], ppids: [post.ppPageId], bucketId: post.bucketId })
  }, [])

  const onPostClick = useCallback((post: PlannedPost) => {
    setWithPreview(true)
    setPostInPreview(post)
  }, [])

  const selectNextPostForPreview = useCallback(() => {
    if (previewSelection.hasNext) {
      const next = posts[previewSelection.selectedPostIndex + 1]
      setPostInPreview(next)
    }
  }, [previewSelection, posts])

  const selectPrevPostForPreview = useCallback(() => {
    if (previewSelection.hasPrev) {
      const prev = posts[previewSelection.selectedPostIndex - 1]
      setPostInPreview(prev)
    }
  }, [previewSelection, posts])

  useEffect(() => {
    if (props.activeView === 'posted') {
      setWithPreview(false)
      setPostInPreview(null)
      setSearch({ query: '', types: POST_TYPES, searchInSummary: true })
    }
  }, [props.activeView])

  useEffect(() => {
    setWithPreview(false)
    setPostInPreview(null)
  }, [selectedProfilePpid])

  useEffect(() => {
    fetch$.current = new Subject()
    fetch$.current
      .pipe(
        filter(() => hasNextRef.current && Boolean(ppidRef.current)),
        tap(() => {
          setLoading(true)
        }),
        mergeMap((data) => {
          if (data.type === 'posted') {
            const sortString = data.sortBy ? PostedSortBy[data.sortBy].toLowerCase() : undefined
            return dispatch(getPostedPosts({
              page: pageRef.current,
              pageIds: [],
              sortBy: sortString,
              ppids: [ppidRef.current],
              bucketId: props.bucket.id,
              query: data.query,
              types: data.types,
              sortDirection: data.sortDirection,
              searchInArticleSummary: data.searchInSummary,
              archivedOnly: data.archivedOnly
            })) as any
          }
          return dispatch(getBucketPosts(props.bucket.id, pageRef.current, ppidRef.current, data.query, data.types, data.searchInSummary))
            .pipe(catchError((e) => {
              return observableOf({ posts: [], error: e })
            }))
        })
      )
      .subscribe((response: any) => {
        setLoading(false)
        setLoadingOverlayActive(false)
        setIsEmptyViewClosed(false)
        if (response.error) {
          dispatch(message(intl.formatMessage({ id: 'post.notifications.bucket-posts-fetch-failed' }), 'error'))
          hasNextRef.current = false
          return
        }
        hasNextRef.current = response.paging.pages > response.page + 1
        // NOTE: update total posts count if it's posted posts or all post types are fetched
        if (!response.request?.planned || response.request?.types?.length === POST_TYPES.length) {
          setTotalPosts(response.totalPostsCount)
        }
        if (onBucketPostsUpdated) {
          onBucketPostsUpdated()
        }
        const postsWithBucket = response.posts.map((p: PlannedPost) => ({ ...p, bucket: props.bucket }))
        if (response.page === 0) {
          postsDispatch(setPosts(postsWithBucket))
        } else {
          postsDispatch(addPosts(postsWithBucket))
        }

        if (response.slots) {
          setAutoPostSlots(current => response.page === 0 ? response.slots : [...current, ...response.slots])
        }
      })

    return () => {
      fetch$.current?.unsubscribe()
    }
  }, [dispatch, props.bucket, intl, onBucketPostsUpdated])

  // Check for newly created posts. Reload posts to fetch the new records
  useEffectUpdateOnly(() => {
    if (location.state?.refresh && props.activeView === 'planned' && !location.state?.updatedPostHash) {
      hasNextRef.current = true
      fetch$.current?.next({ type: props.activeView })
    }
  }, [location.state?.refresh, props.activeView])

  useEffect(() => {
    if (newPostTimestamp) {
      setRefreshKey(current => current + 1)
    }
  }, [newPostTimestamp])

  useEffect(() => {
    if (!props.bucket.ppids.includes(selectedProfilePpid as any)) {
      setSelectedProfilePpid(props.bucket.ppids[0])
    }
  }, [props.bucket.ppids, selectedProfilePpid])

  useEffect(() => {
    if (props.activeView !== 'failed' && bucketPpidsString.split(',').includes(selectedProfilePpid as any)) {
      pageRef.current = 0
      hasNextRef.current = true
      postsDispatch(setPosts([]))
      fetch$.current?.next({
        type: props.activeView,
        query: search.query,
        types: search.types,
        sortBy,
        sortDirection,
        searchInSummary: search.searchInSummary
      })
    }
  }, [
    props.bucket.id, selectedProfilePpid, refreshKey, props.activeView, bucketPpidsString,
    search.query, search.searchInSummary, search.types, sortBy, sortDirection
  ])

  useEffect(() => {
    delete$.current = new Subject()
    delete$.current
      .pipe(
        tap((data) => {
          setLoadingOverlayActive(true)
          setPostInPreview(current => {
            if (data.postIds.includes(current?.id as any)) {
              return null
            }
            return current
          })
        }),
        mergeMap(({ ppids, postIds, all, query, types }) => {
          return dispatch(bulkDeletePosts(postIds, ppids, 'queued', all, props.bucket.id, query, types, 'bucket', refreshPosts))
            .pipe(catchError((error: Error) => {
              dispatch(message(intl.formatMessage({ id: 'post.notifications.delete-posts-error' }, { error: error.message }), 'error'))
              return observableOf({ error })
            }))
        })
      )
      .subscribe((response: any) => {
        if (!response.error) {
          setSelectedPostsIds({})
          setRefreshKey(current => current + 1)
        }
        dispatch(getBucketById(props.bucket.id)).unwrap()
        setLoadingOverlayActive(false)
      })

    return () => delete$.current?.unsubscribe()
  }, [props.bucket.id, props.activeView, dispatch, intl, refreshPosts])

  useEffect(() => {
    shuffle$.current = new Subject()
    shuffle$.current.pipe(
      tap(() => {
        setLoadingOverlayActive(true)
        dispatch(message(intl.formatMessage({ id: 'post.notifications.shuffling-posts' })))
      }),
      mergeMap(({ postIds, ppids, all }) => dispatch(shufflePosts(postIds, ppids, 'planned', all, props.bucket.id)).pipe(catchError(() => {
        return observableOf({ error: true })
      })))
    )
      .subscribe(response => {
        setLoadingOverlayActive(false)
        if (!response.error) {
          setSelectedPostsIds({})
          setRefreshKey(current => current + 1)
        }
      })

    return () => shuffle$.current?.unsubscribe()
  }, [dispatch, props.bucket.id, intl])

  useEffect(() => {
    reorder$.current = new Subject()
    reorder$.current.pipe(
      tap(() => {
        setLoadingOverlayActive(true)
      }),
      exhaustMap(({ ids, moveTo }) => dispatch(reorderPosts(ids, moveTo)).pipe(catchError(() => {
        dispatch(message(intl.formatMessage({ id: 'post.notifications.update-failed' }), 'error'))
        return observableOf({ error: true })
      })))
    )
      .subscribe((res) => {
        if (res.error) {
          refreshPosts()
        }
        setLoadingOverlayActive(false)
      })

    return () => reorder$.current?.unsubscribe()
  }, [dispatch, intl, refreshPosts])

  useEffect(() => {
    recycle$.current = new Subject()
    recycle$.current
      .pipe(
        mergeMap(({ postId, value }) => dispatch(updatePostRecycle(postId, value))
          .pipe(catchError((e) => {
            const msg = e.response?.error.message || intl.formatMessage({ id: 'errors.generic' })
            dispatch(message(msg, 'error'))
            return observableOf({ error: true })
          }))
        )
      )
      .subscribe(response => {
        if (!response.error) {
          setSelectedPostsIds({})
        }
        postsDispatch(updateRecyclePost(response.postId, response.value))
      })

    return () => recycle$.current?.unsubscribe()
  }, [dispatch, intl])

  useEffect(() => {
    const profile = Object.values(connectedProfiles).find(p => p.ppid === selectedProfilePpid)
    if (!selectedProfilePpid || !profile) {
      return
    }

    if (selectedPostsIds === 'all') {
      selectionRef.current = {
        all: true,
        postIds: posts.map(p => p.id),
        ppids: [selectedProfilePpid],
        profileIds: [profile.idNew]
      }
    } else {
      selectionRef.current = {
        all: false,
        ppids: [selectedProfilePpid],
        postIds: Object.keys(selectedPostsIds).filter(id => selectedPostsIds[id]),
        profileIds: [profile.idNew]
      }
    }
  }, [selectedPostsIds, posts, connectedProfiles, selectedProfilePpid])

  useEffect(() => {
    requeue$.current = new Subject()
    requeue$.current.pipe(concatMap(({ postIds, ppids, all, query, types }) => {
      return dispatch(requeuePost(postIds, ppids, 'posted', all, props.bucket.id, query, types))
        .pipe(catchError((error) => {
          if (error.message) {
            dispatch(message(error.message, 'error', NOTIFICATION_DURATION_LONG))
          } else {
            dispatch(message(intl.formatMessage({ id: 'errors.generic' }), 'error'))
          }
          return observableOf({ error })
        }))
    }))
      .subscribe((response: any) => {
        if (!response.error) {
          dispatch(message(intl.formatMessage({ id: 'notifications.post-replanned' }), 'success'))
        }
      })

    return () => requeue$.current?.unsubscribe()
  }, [dispatch, props.bucket.id, intl])

  useEffect(() => {
    batchRecycle$.current = new Subject()
    batchRecycle$.current.pipe(
      tap(() => {
        dispatch(message(intl.formatMessage({ id: 'post.notifications.recycling-posts' })))
      }),
      mergeMap(({ ppids, postIds, all, query, types, value }) => {
        return dispatch(recyclePosts(postIds, ppids, 'planned', all, props.bucket.id, query, types, value))
          .pipe(catchError((e) => {
            const msg = e.response?.error.message || intl.formatMessage({ id: 'errors.generic' })
            dispatch(message(msg, 'error'))
            return observableOf({ error: true })
          }))
      })
    )
      .subscribe(response => {
        if (!response.error) {
          setSelectedPostsIds({})
          postsDispatch(recyclePostsSuccess(response.postIds, response.all))
          for (const postId of response.postIds) {
            postsDispatch(updateRecyclePost(postId, response.recycle))
          }
        }
      })

    return () => batchRecycle$.current?.unsubscribe()
  }, [dispatch, props.bucket.id, intl])

  const loadNextPage = useCallback(() => {
    if (hasNextRef.current && !loading && props.activeView !== 'failed') {
      pageRef.current += 1
      fetch$.current?.next({
        type: props.activeView,
        sortBy,
        sortDirection,
        ...search
      })
    }
  }, [props.activeView, loading, search, sortBy, sortDirection])

  const onDeletePost = useCallback((post: string | PlannedPost) => {
    const id = typeof post === 'string' ? post : post.id
    if (selectedProfilePpid) {
      delete$.current?.next({
        postIds: [id],
        ppids: [selectedProfilePpid],
        all: false,
        query: search.query,
        types: search.types
      })
    }
  }, [selectedProfilePpid, search.query, search.types])

  const profiles = useMemo(() => {
    return Object.values(connectedProfiles).filter(profile => props.bucket.ppids.includes(profile.ppid))
  }, [connectedProfiles, props.bucket.ppids])

  const onProfileClick = useCallback((ppid: string) => {
    setSelectedProfilePpid(ppid)
  }, [])

  const toggleAllPostsSelected = useCallback(() => {
    setSelectedPostsIds(current => current === 'all' ? {} : 'all')
  }, [])

  const deleteSelectedPosts = useCallback((forAllProfiles?: boolean) => {
    delete$.current?.next({
      ...(selectionRef.current as any),
      ppids: forAllProfiles ? props.bucket.ppids : selectionRef.current?.ppids,
      query: search.query,
      types: search.types
    })
  }, [search.query, search.types, props.bucket.ppids])

  const selectedProfileActiveSlotsCount = useMemo(() => {
    return props.bucket.pages.find(p => p.ppid === selectedProfilePpid)?.slotsCount || 0
  }, [props.bucket.pages, selectedProfilePpid])

  const onShuffle = useCallback((applyToAllProfiles?: boolean) => {
    const shuffleAvailable = dispatch(checkFeatureAvailability(FEATURE_POST_SHUFFLE))
    if (!shuffleAvailable) {
      return
    }
    
    if (applyToAllProfiles) {
      const haveSlots = props.bucket.pages.some(p => p.slotsCount && p.slotsCount > 0)
      if (!haveSlots) {
        dispatch(message(intl.formatMessage({ id: 'post.buckets.notification.shuffle-no-slots' }), 'warning', NOTIFICATION_DURATION_LONG))
        return
      }
    } else if (selectedProfileActiveSlotsCount === 0) {
      dispatch(message(intl.formatMessage({ id: 'post.buckets.notification.shuffle-no-slots' }), 'warning', NOTIFICATION_DURATION_LONG))
      return
    }

    const selection = selectionRef.current
    if (selection && (selection.all || selection.postIds.length > 0)) {
      const ppids = applyToAllProfiles ? bucketPpidsString.split(',') : selection.ppids
      shuffle$.current?.next({ ...selection, ppids })
    }
  }, [dispatch, selectedProfileActiveSlotsCount, props.bucket.pages, intl, bucketPpidsString])

  const onCopyPosts = useCallback(() => {
    const copyAllowed = dispatch(checkFeatureAvailability(FEATURE_POST_COPY))
    if (copyAllowed) {
      if (selectionRef.current?.all) {
        setPostsToCopy(postsRef.current)
      } else if (selectionRef.current?.postIds) {
        setPostsToCopy(postsRef.current.filter(p => selectionRef.current?.postIds.includes(p.id)))
      }
    }
  }, [dispatch])

  const togglePostSelected = useCallback((id: string) => {
    setSelectedPostsIds(current => {
      if (current === 'all') {
        return postsRef.current.reduce((selection: Record<string, boolean>, post) => {
          selection[post.id] = post.id !== id
          return selection
        }, {})
      }
      return { ...current, [id]: !current[id] }
    })
  }, [])

  const movePost = useCallback((post: PlannedPost, position: 'top' | 'bottom' | 'up' | 'down') => {
    const reordered = getMovedPostResult(post, position, postsRef.current, autoPostSlotsRef.current)
    if (reordered) {
      const { posts: reorderedPosts, ids, moveTo } = reordered
      postsDispatch(setPosts(reorderedPosts))
      reorder$.current?.next({ ids, moveTo })
    }
  }, [])

  const onReorderPosts = useCallback((movedPost: PlannedPost, targetPost: PlannedPost) => {
    const reordered = getReorderedPosts(movedPost, targetPost, postsRef.current, autoPostSlotsRef.current)
    if (reordered) {
      const { posts: reorderedPosts, ids } = reordered
      reorder$.current?.next({ ids })
      postsDispatch(setPosts(reorderedPosts))
    }
  }, [])

  const loadPostInComposer = useCallback((post: PlannedPost | PostedPost, type?: 'planned' | 'posted') => {
    dispatch(resetComposer())
    dispatch(mergeState(getDerivedStateFromPost(post, type, type === 'planned' ? 'queued' : undefined)))
    navigate('/composer', { state: { floating: true, modal: 'compose', opener: location.pathname, search, background: location } })
  }, [dispatch, location, navigate, search])

  const onCopyPost = useCallback((post: PlannedPost | PostedPost) => {
    const copyAllowed = dispatch(checkFeatureAvailability(FEATURE_POST_COPY))
    if (copyAllowed) {
      setPostsToCopy([post as any])
    }
  }, [dispatch])

  const onRecycleChange = useCallback((id: string, value: number | boolean | Date) => {
    const recycleAvailable = dispatch(checkFeatureAvailability(FEATURE_POST_RECYCLE))
    if (!recycleAvailable) {
      return
    }
    recycle$.current?.next({ postId: id, value })
  }, [dispatch])

  const scrollElement = useMemo(() => {
    return document.querySelector('[data-test="main"]') as HTMLElement
  }, [])

  const closePostMediaPreviewDialog = useCallback(() => {
    setPostInMediaPreviewDialog(null)
  }, [])

  const closeCopyPostsPopup = useCallback((updated?: boolean) => {
    setPostsToCopy([])
    setSelectedPostsIds({})
    if (updated) {
      dispatch(getBuckets())
    }
  }, [dispatch])

  const onFilter = useCallback((query: string, types: ContentType[], searchInSummary: boolean) => {
    setSearch({ query, types, searchInSummary })
    setSelectedPostsIds({})
  }, [])

  const togglePreview = () => {
    setWithPreview(current => !current)
  }

  useEffect(() => {
    if (withPreview) {
      const firstNonEmptyPost = posts.find(p => !p.isEmpty)

      setPostInPreview(current => {
        const postInPreview = current ? posts.find(p => p.id === current.id) : null
        if (!postInPreview && firstNonEmptyPost) {
          return firstNonEmptyPost
        }
        if (!firstNonEmptyPost) {
          return null
        }
        return postInPreview || null
      })
    } else {
      setPostInPreview(null)
    }
  }, [withPreview, posts])

  const onSidebarSelectionChange = useCallback((ids: string[]) => {
    const ppid = profiles.find(p => p.id === ids[0])?.ppid
    if (ppid) {
      setSelectedProfilePpid(ppid)
    }
  }, [profiles])

  const closeBucketUpdatePopup = (refresh?: boolean) => {
    setUpdatePopupOpen(false)
    if (refresh) {
      setRefreshKey(refreshKey + 1)
    }
  }

  const openBucketUpdatePopup = () => {
    setUpdatePopupOpen(true)
  }

  const sidebarContainer = useMemo(() => document.querySelector('[data-buckets-nav]'), [])

  const sidebarProfileSelector = useMemo(() => {
    if (!selectedProfilePpid) {
      return null
    }

    const profilesMap = profiles.reduce((map: any, p) => {
      map[p.ppid] = p
      return map
    }, {})

    return (
      <div className={styles['sidebar-panel']}>
        <div className={styles['sidebar-title']}>
          <FormattedMessage id="general.labels.profiles" />
        </div>
        <SidebarProfileSelector
          profiles={profilesMap}
          activeView={sidebarActiveView}
          selectedProfileIds={[profilesMap[selectedProfilePpid]?.id].filter(Boolean)}
          setActiveView={setSidebarActiveView}
          onSelectedProfilesChange={onSidebarSelectionChange}
        />
      </div>
    )
  }, [profiles, selectedProfilePpid, onSidebarSelectionChange, sidebarActiveView])

  const onRepost = useCallback((post: PostedPost) => {
    loadPostInComposer(post, 'posted')
  }, [loadPostInComposer])

  const onRequeue = useCallback((id: string, ppid: string) => {
    const requeueAvailable = dispatch(checkFeatureAvailability(FEATURE_POST_INSIGHTS_ACTIONS_REPLAN))
    if (!requeueAvailable) {
      return
    }

    requeue$.current?.next({ postIds: [id], ppids: [ppid], all: false })
  }, [dispatch])

  const requeuePosts = useCallback(() => {
    const requeueAvailable = dispatch(checkFeatureAvailability(FEATURE_POST_INSIGHTS_ACTIONS_REPLAN))
    if (!requeueAvailable || selectionRef.current?.postIds.length === 0) {
      return
    }
    requeue$.current?.next({
      postIds: selectionRef.current?.postIds || [],
      ppids: selectionRef.current?.ppids || [],
      all: Boolean(selectionRef.current?.all),
      query: search.query,
      types: search.types
    })
  }, [dispatch, search.query, search.types])

  const bucketProfiles = useMemo(() => {
    const groupedVisibleProfiles = profiles.reduce((map: Record<string, PostDestination[]>, profile: PostDestination) => {
      if (map[profile.type]) {
        map[profile.type].push(profile)
      } else {
        map[profile.type] = [profile]
      }
      return map
    }, {})
    const groupedVisibleProfilesArray: PostDestination[] = []
    Object.keys(groupedVisibleProfiles).sort(sortNetworksDefault).forEach(network => {
      groupedVisibleProfilesArray.push(...groupedVisibleProfiles[network].sort(sortByKeyAlphabetically('name')))
    })
    return groupedVisibleProfilesArray
  }, [profiles])

  const selectedProfile = bucketProfiles.find(p => p.ppid === selectedProfilePpid)

  const selectedPostsCount = useMemo(() => {
    return selectedPostsIds !== 'all'
      ? Object.values(selectedPostsIds).filter(Boolean).length
      : totalPosts
  }, [totalPosts, selectedPostsIds])

  const onSortChange = useCallback((value: PostedSortBy) => {
    const sortAvailable = dispatch(checkFeatureAvailability(FEATURE_POST_INSIGHTS_ACTIONS_SORT))
    if (!sortAvailable) {
      return
    }

    // If changing the sort key, reset page to 0
    if (value !== sortBy) {
      setSortBy(value)
    }
  }, [sortBy, dispatch])

  const activeProfile = Object.values(connectedProfiles).find(p => p.ppid === selectedProfilePpid)
  const sortMenuDisabled = posts.length === 0 || loading

  const onOpenComposer = () => {
    if (props.bucket.ppids.length > 0) {
      dispatch(setComposerBucket(props.bucket.id))
    }
    dispatch(setComposerProfiles(profiles))
  }

  const onBulkRecycle = useCallback(() => {
    setRecycleDialogOpen(true)
  }, [])

  const closeRecycleDialog = () => {
    setRecycleDialogOpen(false)
  }

  const bulkRecyclePosts = (value: boolean | Date | number) => {
    batchRecycle$.current?.next({
      ...(selectionRef.current as any),
      query: search.query,
      types: search.types,
      value
    })

    closeRecycleDialog()
  }

  const bucketActivePeriod = useMemo(() => {
    if (!props.bucket.activeFrom || !props.bucket.activeTo) {
      return 'always'
    }
    return `${format(props.bucket.activeFrom, 'LLL d, yyyy')} to ${format(props.bucket.activeTo, 'LLL d, yyyy')}`
  }, [props.bucket.activeFrom, props.bucket.activeTo])

  const slotsCountByProfile = useMemo(() => {
    return props.bucket.pages.reduce((acc: Record<string, number>, p) => {
      acc[p.ppid] = p.slotsCount || 0
      return acc
    }, {})
  }, [props.bucket.pages])

  const goToPlan = () => {
    const selectedProfilePlanId = Object.keys(planProfilesMap)
      .find(planId => planProfilesMap[planId].ppids.includes(selectedProfilePpid as string))
    navigate(`/plans/${selectedProfilePlanId || ''}`, { state: { bucketId: props.bucket.id } })
  }

  const postsPerProfile = props.bucket.pages.reduce((acc, p) => {
    acc[p.ppid] = p.plannedPostsCount
    return acc
  }, {} as Record<string, number>)

  const onCopyPostsFinished = () => {
    setRefreshKey(current => current + 1)
    closeCopyPostsPopup(true)
  }

  const onToggleFavorite = useCallback((postId: string, ppid: string, value: boolean) => {
    const action = value ? 'favorite' : 'unfavorite'
    return dispatch(postedAction(action, [postId], [ppid]))
  }, [dispatch])

  const bulkMoveToTop = useCallback(() => {
    if (selectedPostsIds === 'all') {
      setSelectedPostsIds({})
      return
    }
    const movedPosts = Object.keys(selectedPostsIds).filter(id => selectedPostsIds[id])
      .map(id => postsMap.get(id)).filter(Boolean) as PlannedPost[]
    const result = getBulkMovePostsResult(movedPosts, 'top', postsRef.current, autoPostSlotsRef.current)
    if (result) {
      const { posts: updatedPosts, ids } = result
      postsDispatch(setPosts(updatedPosts))
      reorder$.current?.next({ ids })
      setSelectedPostsIds({})
    }
  }, [selectedPostsIds, postsMap])

  const bulkMoveToBottom = useCallback(() => {
    if (selectedPostsIds === 'all') {
      setSelectedPostsIds({})
      return
    }
    const movedPosts = Object.keys(selectedPostsIds).filter(id => selectedPostsIds[id])
      .map(id => postsMap.get(id)).filter(Boolean) as PlannedPost[]
    const result = getBulkMovePostsResult(movedPosts, 'bottom', postsRef.current, autoPostSlotsRef.current)
    if (result) {
      const { posts: updatedPosts, ids } = result
      postsDispatch(setPosts(updatedPosts))
      reorder$.current?.next({ ids })
      setSelectedPostsIds({})
    }
  }, [selectedPostsIds, postsMap])

  const onArchive = useCallback((id: string, ppid: string) => {
    postedAction$.current?.next({ action: 'archive', postIds: [id], ppids: [ppid], all: false })
  }, [])

  const onBulkArchive = useCallback(() => {
    if (selectionRef.current?.postIds.length === 0) {
      return
    }
    postedAction$.current?.next({
      postIds: selectionRef.current?.postIds || [],
      ppids: selectionRef.current?.ppids || [],
      all: Boolean(selectionRef.current?.all),
      query: search.query,
      types: search.types,
      action: 'archive'
    })
  }, [search.query, search.types])

  const withBulkMove = selectedPostsIds !== 'all' && selectedPostsCount > 1 && selectedPostsCount < totalPosts

  const plannedBulkActionsProps = useMemo<BulkPostActionsProps>(() => ({
    hidePostActions: false,
    withDelete: true,
    withCopy: true,
    withShuffle: true,
    bucketMode: true,
    withRecycle: true,
    selectedCount: selectedPostsCount,
    disabled: selectedPostsCount === 0,
    allSelected: selectedPostsIds === 'all',
    splitDeleteAll: true,
    splitShuffleAll: true,
    skipDeleteConfirm: true, // undo snackbar available (onRestored passed to bulkDeletePosts)
    onToggleAllSelected: toggleAllPostsSelected,
    onBulkDeletePosts: deleteSelectedPosts,
    onShuffle,
    onCopy: onCopyPosts,
    onBulkRecycle,
    onMoveToTop: withBulkMove ? bulkMoveToTop : undefined,
    onMoveToBottom: withBulkMove ? bulkMoveToBottom : undefined
  }), [
    selectedPostsCount, selectedPostsIds, toggleAllPostsSelected, deleteSelectedPosts,
    onShuffle, onCopyPosts, onBulkRecycle, withBulkMove, bulkMoveToTop, bulkMoveToBottom
  ])

  const postedBulkActionsProps = useMemo<BulkPostActionsProps>(() => ({
    hidePostActions: false,
    withCopy: true,
    withRequeue: true,
    bucketMode: true,
    selectedCount: selectedPostsCount,
    disabled: selectedPostsCount === 0,
    allSelected: selectedPostsIds === 'all',
    onToggleAllSelected: toggleAllPostsSelected,
    onBulkRequeue: requeuePosts,
    onCopy: onCopyPosts,
    onArchive: onBulkArchive
  }), [
    selectedPostsCount, selectedPostsIds, toggleAllPostsSelected, requeuePosts,
    onCopyPosts, onBulkArchive
  ])

  return (
    <section>
      <header className={styles.header}>
        <div className={styles.grid}>
          <BucketCard bucket={props.bucket} noSelection onEdit={openBucketUpdatePopup} />
          <div className={styles.right}>
            <div className={styles['bucket-info']}>
              <span className={`${styles.label} text-ellipsis`}>
                <FormattedMessage
                  id="post.buckets.subtitle-status-period"
                  values={{
                    status: props.bucket.isExpired ? 'inactive' : 'active',
                    period: bucketActivePeriod,
                    s: (text: any) => (
                      <b onClick={openBucketUpdatePopup} className={styles[`label-${props.bucket.isExpired ? 'red' : 'green'}`]}>
                        {text}
                      </b>
                    ),
                    p: (text: any) => (
                      <b onClick={openBucketUpdatePopup} className={styles[`label-${props.bucket.isExpired ? 'red' : 'green'}`]}>
                        {text}
                      </b>
                    )
                  }}
                />
              </span>
              <span className={`${styles.label} text-ellipsis`}>
                <FormattedMessage
                  id="post.buckets.subtitle-profiles"
                  values={{
                    count: props.bucket.ppids.length,
                    b: (text: any) => <b onClick={openBucketUpdatePopup} className={styles['label-green']}>{text}</b>
                  }}
                />
              </span>
              <span className={`${styles.label} text-ellipsis`}>
                <FormattedMessage
                  id="post.buckets.subtitle-networks"
                  values={{
                    networks: () => (
                      <div className={styles.networks}>
                        {Object.keys(groupByKey(bucketProfiles, 'type')).map((network: PostDestinationType) => (
                          <SocialIcon icon={network} circle key={network} />
                        ))}
                      </div>
                    )
                  }}
                />
              </span>
              <span className={`${styles.label} text-ellipsis`}>
                <FormattedMessage
                  id="post.buckets.subtitle-total-posts"
                  values={{
                    count: props.bucket.postsCount,
                    b: (text: any) => (
                      <b>{text}</b>
                    )
                  }}
                />
              </span>
            </div>
            <div className={styles['profile-info']}>
              <div className={styles['profile-icon']}>
                {selectedProfile && (
                  <BucketProfile
                    key={selectedProfile.id}
                    profile={selectedProfile}
                    postsCount={totalPosts}
                    isSelected
                    postingTimesCount={selectedProfileActiveSlotsCount}
                    onClick={() => { }}
                  />
                )}
              </div>
              <div className={styles['profile-info-content']}>
                <span className={`${styles.label} text-ellipsis`}>
                  <FormattedMessage
                    id={selectedProfileActiveSlotsCount === 0 ? 'post.buckets.subtitle-slots-none' : 'post.buckets.subtitle-slots'}
                    values={{
                      count: selectedProfileActiveSlotsCount,
                      b: (text: any) => (
                        <b onClick={goToPlan} className={styles[`label-${selectedProfileActiveSlotsCount === 0 ? 'red' : 'green'}`]}>
                          {text}
                        </b>
                      )
                    }}
                  />
                </span>
                <span className={`${styles.label} text-ellipsis`}>
                  <FormattedMessage
                    id="post.buckets.subtitle-total-posts-profile"
                    values={{
                      count: totalPosts,
                      b: (text: any) => (
                        <b>{text}</b>
                      )
                    }}
                  />
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className={styles['profiles-box']}>
          <div className={styles['profiles-list']}>
            {bucketProfiles.map(p => {
              const isSelected = p.ppid === selectedProfilePpid
              return (
                <BucketProfile
                  key={p.id}
                  profile={p}
                  postsCount={postsPerProfile[p.ppid] || 0}
                  isSelected={isSelected}
                  postingTimesCount={slotsCountByProfile[p.ppid]}
                  onClick={onProfileClick}
                />
              )
            })}
          </div>
        </div>
      </header>
      {!isFailedView && (
        <Fragment>
          <PublishingTopBar
            totalCount={undefined}
            activeTypes={search.types}
            postsCountLabel={(
              <Fragment>
                <span className={styles.count}>
                  <FormattedMessage id="post.buckets.label-total-posts" values={{ count: props.bucket.postsCount }} />
                </span>
                <span className={styles.count}>
                  <FormattedMessage id="post.buckets.label-profile-posts" values={{ count: totalPosts }} />
                </span>
              </Fragment>
            )}
            children={(
              <Fragment>
                {props.activeView === 'planned' ? null : (
                  <PostedSortMenu
                    sortBy={sortBy}
                    sortDirection={sortDirection}
                    network={activeProfile?.type}
                    disabled={sortMenuDisabled}
                    className={styles['posted-sort-menu']}
                    onSortChange={onSortChange}
                    onSortDirectionChange={setSortDirection}
                  />
                )}
                <HelpToggleButton />
              </Fragment>
            )}
            onFilter={onFilter}
            withPreviewToggle={props.activeView === 'planned'}
            showPreview={withPreview}
            className={styles['top-bar']}
            onTogglePreview={togglePreview}
          />
          <ScrollListener emitTreshold={SCROLL_TRESHOLD} scrollElement={scrollElement} onScroll={loadNextPage}>
            <div className={styles.wrapper}>
              <div className={styles.content}>
                {posts.length > 0 && props.activeView === 'planned' && (
                  <PlannedPostsList
                    posts={posts}
                    selectedPosts={selectedPostsIds}
                    bucketsView
                    skipReorderChecks
                    activePreviewPostId={postInPreview?.id}
                    bulkActionsProps={plannedBulkActionsProps}
                    hideMoveUpDown={Boolean(search.query) || search.types.length !== POST_TYPES.length}
                    onLoadNextPage={loadNextPage}
                    onTogglePostSelected={togglePostSelected}
                    onDeletePost={onDeletePost}
                    onEditPost={loadPostInComposer}
                    onCopyPost={onCopyPost}
                    onMediaPreviewBtnClick={setPostInMediaPreviewDialog}
                    onMovePost={movePost}
                    onPostOrderChanged={onReorderPosts}
                    onRecycleChange={onRecycleChange}
                    onPostClick={onPostClick}
                    onShareNow={onShareNow}
                  />
                )}
                {posts.length > 0 && props.activeView === 'posted' && (
                  <PostedPostsList
                    posts={posts as any[]}
                    sortBy={sortBy}
                    selectedPosts={selectedPostsIds}
                    className={styles['posted-list']}
                    bulkActionsProps={postedBulkActionsProps}
                    actionsClassName={styles['actions-posted']}
                    onTogglePostSelected={togglePostSelected}
                    onLoadNextPage={loadNextPage}
                    onMediaPreviewBtnClick={setPostInMediaPreviewDialog}
                    onRepost={onRepost}
                    onRequeuePosted={onRequeue}
                    onCopy={onCopyPost}
                    onToggleFavorite={onToggleFavorite}
                    onArchive={onArchive}
                  />
                )}
                {!loading && posts.length === 0 && !isEmptyViewClosed && (
                  <EmptyView
                    icon={mdiPail}
                    title={<FormattedMessage
                      id={props.activeView === 'planned' ? 'post.buckets.empty-view.title' : 'post.buckets.empty-view-posted.title'}
                    />}
                    subtitle={(
                      <div>
                        <NavLink
                          to={{ pathname: '/composer' }}
                          state={{ floating: true, modal: 'compose', opener: location.pathname, search, background: location }}
                          onClick={onOpenComposer}
                        >
                          Create a new post
                        </NavLink>
                        <span>
                          &nbsp;or&nbsp;
                          {props.activeView === 'planned'
                            ? <NavLink to={`/posts/buckets/${props.bucket.id}/posted`}>add posts from Posted</NavLink>
                            : <NavLink to="/content/search/content">search for content</NavLink>}
                        </span>
                      </div>
                    )}
                    backdrop
                    backdropClassName={styles['empty-view-backdrop']}
                    onClose={closeEmptyView}
                  />
                )}
                {loadingOverlayActive && (
                  <div className={styles['loading-overlay']}>
                    <SimpleLoader small />
                  </div>
                )}
              </div>
              <div
                className={`${styles['preview-box']} ${withPreview ? styles.visible : ''}`}
              >
                {postInPreview && (
                  <PostPreviewWithInfo
                    post={postInPreview}
                    hasNextPost={previewSelection.hasNext}
                    hasPreviousPost={previewSelection.hasPrev}
                    className={styles.preview}
                    onSelectNext={selectNextPostForPreview}
                    onSelectPrevious={selectPrevPostForPreview}
                    skipDeleteConfirm
                    onDelete={onDeletePost}
                    onMove={movePost}
                    onEdit={loadPostInComposer}
                    onCopy={onCopyPost}
                    onRecycleChange={onRecycleChange}
                    onClose={togglePreview}
                    onPostUpdated={refreshPosts}
                  />
                )}
              </div>
            </div>
          </ScrollListener>
        </Fragment>
      )}
      {isFailedView && <FailedPostsManager bucketId={props.bucket.id} fetchIds={selectedProfile?.idNew} />}
      {loading && !loadingOverlayActive && (
        <SimpleLoader small />
      )}
      <PostFilesDialog
        active={Boolean(postInMediaPreviewDialog)}
        images={postInMediaPreviewDialog?.images || []}
        onClose={closePostMediaPreviewDialog}
      />
      <RecycleDialog open={recycleDialogOpen} onClose={closeRecycleDialog} onSubmit={bulkRecyclePosts} />
      <CopyPostsPopup
        open={postsToCopy.length > 0}
        posts={postsToCopy}
        query={search.query}
        types={search.types}
        postType={props.activeView}
        allSelected={Boolean(selectionRef.current?.all)}
        bucketId={props.bucket.id}
        onClose={closeCopyPostsPopup}
        onCopyStarted={closeCopyPostsPopup}
        onCopyFinished={onCopyPostsFinished}
      />
      {updatePopupOpen && (
        <BucketUpdatePopup bucket={props.bucket} onClose={closeBucketUpdatePopup} />
      )}
      {createPortal(sidebarProfileSelector, sidebarContainer as HTMLElement)}
    </section>
  )
}

export default BucketPostsView
